import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Monitor,
  Camera,
  Mic,
  MicOff,
  CameraOff,
  Users,
  Zap,
  Wifi,
  WifiOff,
  LogOut,
  BrainCircuit,
  CheckCircle2,
  XCircle,
  Maximize2,
  Minimize2,
} from "lucide-react";

function getWsUrl(roomId: string, name: string) {
  const baseUrl = import.meta.env.VITE_WS_URL || `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
  const encodedName = encodeURIComponent(name || "Anonymous");
  return `${baseUrl}/ws/relay?role=student&roomId=${roomId}&name=${encodedName}`;
}

interface Quiz {
  question: string;
  options: string[];
  correctIndex: number;
}

export default function StudentPage() {
  const [roomId, setRoomId] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studentCount, setStudentCount] = useState(0);
  const [teacherDisconnected, setTeacherDisconnected] = useState(false);
  const [classEnded, setClassEnded] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [hasScreenFrame, setHasScreenFrame] = useState(false);
  const [hasCameraFrame, setHasCameraFrame] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [nameEntered, setNameEntered] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [panelRatio, setPanelRatio] = useState(66);
  const [maximizedPanel, setMaximizedPanel] = useState<"screen" | "camera" | null>(null);

  const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const screenImgRef = useRef<HTMLImageElement | null>(null);
  const cameraImgRef = useRef<HTMLImageElement | null>(null);
  const audioQueueRef = useRef<number[][]>([]);
  const audioPlayingRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const myCameraStreamRef = useRef<MediaStream | null>(null);
  const myCameraIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("roomId");
    if (!id) {
      setError("No room code provided. Go back to the landing page.");
      return;
    }
    setRoomId(id);
  }, []);

  useEffect(() => {
    if (!roomId || !nameEntered) return;

    const ws = new WebSocket(getWsUrl(roomId, studentName));
    wsRef.current = ws;

    ws.onopen = () => {};

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);

        switch (msg.type) {
          case "joined":
            setJoined(true);
            setStudentCount(msg.studentCount);
            break;

          case "student-count":
            setStudentCount(msg.count);
            break;

          case "screen-frame":
            if (screenImgRef.current && msg.data?.buffer) {
              const blob = new Blob([new Uint8Array(msg.data.buffer)], {
                type: "image/jpeg",
              });
              if (screenImgRef.current.src)
                URL.revokeObjectURL(screenImgRef.current.src);
              screenImgRef.current.src = URL.createObjectURL(blob);
              setHasScreenFrame(true);
            }
            break;

          case "camera-frame":
            if (cameraImgRef.current && msg.data?.buffer) {
              const blob = new Blob([new Uint8Array(msg.data.buffer)], {
                type: "image/jpeg",
              });
              if (cameraImgRef.current.src)
                URL.revokeObjectURL(cameraImgRef.current.src);
              cameraImgRef.current.src = URL.createObjectURL(blob);
              setHasCameraFrame(true);
            }
            break;

          case "audio-chunk":
            playAudioChunk(msg.data?.buffer);
            break;

          case "quiz-active":
            setActiveQuiz(msg.quiz);
            setSelectedAnswer(null);
            setAnswerSubmitted(false);
            break;

          case "clear-quiz":
            setActiveQuiz(null);
            setSelectedAnswer(null);
            setAnswerSubmitted(false);
            break;

          case "teacher-disconnected":
            setTeacherDisconnected(true);
            break;

          case "class-ended":
            setClassEnded(true);
            break;

          case "error":
            setError(msg.message);
            break;
        }
      } catch {}
    };

    ws.onclose = () => {
      if (!joined) setError("Connection lost. Please try again.");
    };

    return () => {
      ws.close();
      stopMyCamera();
    };
  }, [roomId, nameEntered]);

  const playAudioChunk = (buffer: number[]) => {
    if (!buffer?.length) return;
    audioQueueRef.current.push(buffer);
    processAudioQueue();
  };

  const processAudioQueue = async () => {
    if (audioPlayingRef.current || audioQueueRef.current.length === 0) return;
    audioPlayingRef.current = true;

    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new AudioContext({ sampleRate: 16000 });
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") await ctx.resume();

      const int16 = new Int16Array(audioQueueRef.current.shift()!);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
      }

      const audioBuffer = ctx.createBuffer(1, float32.length, 16000);
      audioBuffer.getChannelData(0).set(float32);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      await new Promise<void>((resolve) => {
        source.onended = () => resolve();
        source.start();
      });
    } catch {}

    audioPlayingRef.current = false;
    if (audioQueueRef.current.length > 0) processAudioQueue();
  };

  const startMyCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      myCameraStreamRef.current = stream;

      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      myCameraIntervalRef.current = setInterval(() => {
        if (video.readyState < 2) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            const reader = new FileReader();
            reader.onload = () => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(
                  JSON.stringify({
                    type: "camera-frame",
                    data: {
                      buffer: Array.from(new Uint8Array(reader.result as ArrayBuffer)),
                      ts: Date.now(),
                    },
                  })
                );
              }
            };
            reader.readAsArrayBuffer(blob);
          },
          "image/jpeg",
          0.4
        );
      }, 500);

      setCameraOn(true);
    } catch (err) {
      console.error("Camera failed:", err);
    }
  };

  const stopMyCamera = () => {
    if (myCameraIntervalRef.current) {
      clearInterval(myCameraIntervalRef.current);
      myCameraIntervalRef.current = null;
    }
    if (myCameraStreamRef.current) {
      myCameraStreamRef.current.getTracks().forEach((t) => t.stop());
      myCameraStreamRef.current = null;
    }
    setCameraOn(false);
  };

  const startMyMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new AudioContext({ sampleRate: 16000 });
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        const downsampled = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          downsampled[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "audio-chunk",
              buffer: Array.from(downsampled),
            })
          );
        }
      };

      source.connect(processor);
      processor.connect(audioCtx.destination);

      stream.getAudioTracks()[0].onended = () => {
        audioCtx.close().catch(() => {});
        setMicOn(false);
      };

      audioCtxRef.current = audioCtx;
      setMicOn(true);
    } catch (err) {
      console.error("Mic failed:", err);
    }
  };

  const stopMyMic = () => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setMicOn(false);
  };

  const handleAnswerSelect = (index: number) => {
    if (answerSubmitted) return;
    setSelectedAnswer(index);
    setAnswerSubmitted(true);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: "quiz-answer", answerIndex: index })
      );
    }
  };

  const getOptionStyle = (index: number) => {
    if (!answerSubmitted) {
      return "bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-700 hover:border-slate-500 cursor-pointer";
    }

    const isCorrect = index === activeQuiz!.correctIndex;
    const isSelected = index === selectedAnswer;

    if (isCorrect) {
      return "bg-emerald-500/15 border-emerald-500/50 text-emerald-300 ring-1 ring-emerald-500/30";
    }

    if (isSelected && !isCorrect) {
      return "bg-red-500/15 border-red-500/50 text-red-300 ring-1 ring-red-500/30";
    }

    return "bg-slate-700/30 border-slate-600/30 text-slate-500 opacity-60";
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const ratio = Math.min(Math.max((x / rect.width) * 100, 20), 80);
      setPanelRatio(ratio);
    };

    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen();
      else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
      setIsFullscreen(true);
      setMaximizedPanel(null);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <WifiOff size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Connection Error
          </h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition"
          >
            <LogOut size={16} /> Back to Home
          </a>
        </div>
      </div>
    );
  }

  if (!joined) {
    if (!nameEntered) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Join Class</h2>
            <p className="text-slate-400 mb-4">
              Room:{" "}
              <span className="font-mono font-bold text-emerald-400">
                {roomId}
              </span>
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (studentName.trim()) {
                  setNameEntered(true);
                }
              }}
              className="space-y-4"
            >
              <input
                type="text"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="Enter your name"
                maxLength={30}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                autoFocus
              />
              <button
                type="submit"
                disabled={!studentName.trim()}
                className="w-full px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Join Class
              </button>
            </form>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Joining Class</h2>
          <p className="text-slate-400">
            Connecting to room{" "}
            <span className="font-mono font-bold text-emerald-400">
              {roomId}
            </span>
            ...
          </p>
        </div>
      </div>
    );
  }

  if (teacherDisconnected) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <WifiOff size={32} className="text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Class Has Ended
          </h2>
          <p className="text-slate-400 mb-6">
            The teacher has left the classroom.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition"
          >
            <LogOut size={16} /> Back to Home
          </a>
        </div>
      </div>
    );
  }

  if (classEnded) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <WifiOff size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Class Has Ended
          </h2>
          <p className="text-slate-400 mb-6">
            The teacher has ended the session.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition"
          >
            <LogOut size={16} /> Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="text-white" size={18} fill="currentColor" />
            </div>
            <span className="text-lg font-bold">EduPulse AI</span>
            <span className="text-xs bg-emerald-600/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
              Student
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-slate-700/50 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-white">{studentName}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-700/50 rounded-lg px-3 py-2">
              <span className="text-xs text-slate-400">Room</span>
              <span className="font-mono font-bold text-sm text-emerald-400">
                {roomId}
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-700/50 rounded-lg px-3 py-2">
              <Users size={14} className="text-emerald-400" />
              <span className="text-sm">{studentCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Wifi size={14} className="text-emerald-400" />
              <span className="text-xs text-slate-400">Live</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-4">
        <div className="relative" ref={containerRef}>
          <button
            onClick={toggleFullscreen}
            className="absolute top-2 right-2 z-10 p-2 bg-slate-700/80 hover:bg-slate-600 rounded-lg text-slate-300 hover:text-white transition"
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>

          <div className="flex gap-1" style={{ minHeight: "400px" }}>
            <div
              className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden flex flex-col"
              style={{
                width: isFullscreen || maximizedPanel === "screen"
                  ? "100%"
                  : maximizedPanel === "camera"
                  ? "0%"
                  : `${panelRatio}%`,
                display: maximizedPanel === "camera" ? "none" : "flex",
              }}
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                <Monitor size={16} className="text-indigo-400" />
                <span className="text-sm font-medium">Teacher's Screen</span>
                <button
                  onClick={() => {
                    setMaximizedPanel(
                      maximizedPanel === "screen" ? null : "screen"
                    );
                  }}
                  className="ml-auto p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition"
                  title={
                    maximizedPanel === "screen"
                      ? "Restore"
                      : "Maximize"
                  }
                >
                  {maximizedPanel === "screen" ? (
                    <Minimize2 size={14} />
                  ) : (
                    <Maximize2 size={14} />
                  )}
                </button>
              </div>
              <div className="flex-1 bg-slate-900 flex items-center justify-center relative min-h-[300px]">
                <img
                  ref={screenImgRef}
                  className={`w-full h-full object-contain ${
                    hasScreenFrame ? "" : "hidden"
                  }`}
                  alt="Teacher screen"
                />
                {!hasScreenFrame && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <Monitor size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">
                        Waiting for teacher to share screen...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!isFullscreen && !maximizedPanel && (
              <div
                className="w-1.5 bg-slate-700 hover:bg-indigo-500 cursor-col-resize rounded-full transition-colors flex-shrink-0"
                onMouseDown={handleResizeStart}
              />
            )}

            {!isFullscreen && maximizedPanel !== "screen" && (
              <div
                className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden flex flex-col"
                style={{
                  width: maximizedPanel === "camera"
                    ? "100%"
                    : `${100 - panelRatio}%`,
                }}
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                  <Camera size={16} className="text-violet-400" />
                  <span className="text-sm font-medium">Teacher Camera</span>
                  <button
                    onClick={() => {
                      setMaximizedPanel(
                        maximizedPanel === "camera" ? null : "camera"
                      );
                    }}
                    className="ml-auto p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition"
                    title={
                      maximizedPanel === "camera"
                        ? "Restore"
                        : "Maximize"
                    }
                  >
                    {maximizedPanel === "camera" ? (
                      <Minimize2 size={14} />
                    ) : (
                      <Maximize2 size={14} />
                    )}
                  </button>
                </div>
                <div className="flex-1 bg-slate-900 flex items-center justify-center relative min-h-[300px]">
                  <img
                    ref={cameraImgRef}
                    className={`w-full h-full object-contain ${
                      hasCameraFrame ? "" : "hidden"
                    }`}
                    alt="Teacher camera"
                  />
                  {!hasCameraFrame && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <Camera size={48} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Camera off</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {activeQuiz && (
          <div className="bg-slate-800 rounded-2xl border border-amber-500/30 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700 bg-amber-500/5">
              <BrainCircuit size={16} className="text-amber-400" />
              <span className="text-sm font-medium text-amber-400">
                Quiz Time
              </span>
              {answerSubmitted && (
                <span
                  className={`ml-auto flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
                    selectedAnswer === activeQuiz.correctIndex
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {selectedAnswer === activeQuiz.correctIndex ? (
                    <>
                      <CheckCircle2 size={12} /> Correct!
                    </>
                  ) : (
                    <>
                      <XCircle size={12} /> Incorrect
                    </>
                  )}
                </span>
              )}
            </div>

            <div className="p-4 space-y-3">
              <p className="text-sm font-medium text-white leading-relaxed">
                {activeQuiz.question}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeQuiz.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswerSelect(i)}
                    disabled={answerSubmitted}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 ${getOptionStyle(
                      i
                    )}`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        answerSubmitted && i === activeQuiz.correctIndex
                          ? "bg-emerald-500 text-white"
                          : answerSubmitted &&
                            i === selectedAnswer &&
                            i !== activeQuiz.correctIndex
                          ? "bg-red-500 text-white"
                          : "bg-slate-600 text-slate-300"
                      }`}
                    >
                      {answerSubmitted && i === activeQuiz.correctIndex ? (
                        <CheckCircle2 size={14} />
                      ) : answerSubmitted &&
                        i === selectedAnswer &&
                        i !== activeQuiz.correctIndex ? (
                        <XCircle size={14} />
                      ) : (
                        String.fromCharCode(65 + i)
                      )}
                    </span>
                    <span className="text-left">{opt}</span>
                  </button>
                ))}
              </div>

              {!answerSubmitted && (
                <p className="text-xs text-slate-500 text-center">
                  Select an answer above
                </p>
              )}
            </div>
          </div>
        )}

        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-400">Your Controls</p>
            <div className="flex items-center gap-2">
              <button
                onClick={cameraOn ? stopMyCamera : startMyCamera}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  cameraOn
                    ? "bg-violet-600/20 text-violet-400 hover:bg-violet-600/30"
                    : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                }`}
              >
                {cameraOn ? <Camera size={16} /> : <CameraOff size={16} />}
                {cameraOn ? "Camera On" : "Camera Off"}
              </button>
              <button
                onClick={micOn ? stopMyMic : startMyMic}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  micOn
                    ? "bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30"
                    : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                }`}
              >
                {micOn ? <Mic size={16} /> : <MicOff size={16} />}
                {micOn ? "Mic On" : "Mic Off"}
              </button>
            </div>
          </div>

          {cameraOn && (
            <div className="bg-slate-900 rounded-xl overflow-hidden">
              <video
                ref={(el) => {
                  if (el && myCameraStreamRef.current) {
                    el.srcObject = myCameraStreamRef.current;
                    el.play().catch(() => {});
                  }
                }}
                className="w-full max-h-48 object-contain"
                muted
                autoPlay
                playsInline
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
